import React, { useState, useEffect, useCallback } from 'react';
import {
  FaStore, FaUsers, FaQrcode, FaCalendarCheck,
  FaBullhorn, FaMoneyBillWave, FaFileExcel, FaChevronDown,
  FaChevronUp, FaStar, FaFire,
} from 'react-icons/fa';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, LineChart, Line,
} from 'recharts';
import { supabase } from '../services/supabase';
import { useAuth } from '../context/AuthContext';

const exportToCSV = (rows, filename) => {
  if (!rows.length) return;
  const keys = Object.keys(rows[0]);
  const csv = [
    keys.join(','),
    ...rows.map(r => keys.map(k => `"${String(r[k] ?? '').replace(/"/g, '""')}"`).join(',')),
  ].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
};

function StatCard({ icon: Icon, label, value, color, sub }) {
  return (
    <div style={{ ...s.statCard, borderLeft: `4px solid ${color}` }}>
      <div style={{ ...s.statIcon, background: `${color}18` }}>
        <Icon size={24} color={color} />
      </div>
      <div>
        <div style={s.statLabel}>{label}</div>
        <div style={s.statValue}>{value}</div>
        {sub && <div style={s.statSub}>{sub}</div>}
      </div>
    </div>
  );
}

function PeriodSelector({ value, onChange }) {
  return (
    <select value={value} onChange={e => onChange(e.target.value)} style={s.select}>
      <option value="this_month">Bu ay</option>
      <option value="last_month">Keçən ay</option>
      <option value="last_3months">Son 3 ay</option>
      <option value="all">Bütün tarix</option>
    </select>
  );
}

function getDateRange(period) {
  const now = new Date();
  let start;
  if (period === 'this_month') {
    start = new Date(now.getFullYear(), now.getMonth(), 1);
  } else if (period === 'last_month') {
    start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const end = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59);
    return { start: start.toISOString(), end: end.toISOString() };
  } else if (period === 'last_3months') {
    start = new Date(now.getFullYear(), now.getMonth() - 3, 1);
  } else {
    start = new Date('2024-01-01');
  }
  return { start: start.toISOString(), end: now.toISOString() };
}

// ─── Business row with expandable detail ────────────────────────────────────
function BusinessRow({ biz, period }) {
  const [expanded, setExpanded] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const loadDetail = useCallback(async () => {
    if (detail) return;
    setDetailLoading(true);
    try {
      const { start, end } = getDateRange('last_3months');

      // Daily check-ins for last 3 months
      const { data: ciData } = await supabase
        .from('check_ins')
        .select('created_at')
        .eq('business_id', biz.id)
        .gte('created_at', start)
        .lte('created_at', end)
        .order('created_at', { ascending: true });

      const dailyMap = {};
      (ciData || []).forEach(r => {
        const d = new Date(r.created_at);
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        dailyMap[key] = (dailyMap[key] || 0) + 1;
      });
      const checkInChart = Object.entries(dailyMap).map(([month, count]) => ({ month, count }));

      // Top users for this business
      const { data: topData } = await supabase
        .from('check_ins')
        .select('user_id, users(full_name, username)')
        .eq('business_id', biz.id)
        .limit(500);

      const uMap = {};
      (topData || []).forEach(r => {
        const uid = r.user_id;
        if (!uMap[uid]) uMap[uid] = { name: r.users?.full_name || r.users?.username || '—', count: 0 };
        uMap[uid].count++;
      });
      const topUsers = Object.values(uMap).sort((a, b) => b.count - a.count).slice(0, 5);

      // Active campaigns
      const { data: campData } = await supabase
        .from('campaigns')
        .select('id, title, created_at')
        .eq('business_id', biz.id)
        .order('created_at', { ascending: false })
        .limit(5);

      setDetail({ checkInChart, topUsers, campaigns: campData || [] });
    } catch (e) {
      console.error(e);
    } finally {
      setDetailLoading(false);
    }
  }, [biz.id, detail]);

  const toggle = () => {
    if (!expanded) loadDetail();
    setExpanded(v => !v);
  };

  return (
    <>
      <tr style={s.row} onClick={toggle}>
        <td style={s.td}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={s.expandIcon}>{expanded ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}</span>
            <strong>{biz.name}</strong>
          </div>
          <div style={s.tdSub}>{biz.city || '—'}</div>
        </td>
        <td style={s.td}>
          <span style={{ ...s.badge, background: '#e8f5e9', color: '#2e7d32' }}>
            {biz.revenue.toFixed(2)} ₼
          </span>
        </td>
        <td style={s.td}>{biz.checkIns}</td>
        <td style={s.td}>{biz.qrScans}</td>
        <td style={s.td}>{biz.reservations}</td>
        <td style={s.td}>{biz.campaigns}</td>
        <td style={s.td}>{biz.uniqueUsers}</td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={7} style={s.expandTd}>
            {detailLoading ? (
              <div style={{ padding: 20, color: '#999' }}>Yüklənir...</div>
            ) : detail ? (
              <div style={s.expandContent}>
                {/* Monthly check-in chart */}
                {detail.checkInChart.length > 0 && (
                  <div style={s.detailBlock}>
                    <div style={s.detailTitle}>📊 Aylıq QR Skan (Son 3 ay)</div>
                    <ResponsiveContainer width="100%" height={160}>
                      <BarChart data={detail.checkInChart}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip formatter={v => [`${v} skan`]} />
                        <Bar dataKey="count" fill="#FF6B35" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
                <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
                  {/* Top users */}
                  <div style={{ ...s.detailBlock, flex: 1, minWidth: 220 }}>
                    <div style={s.detailTitle}>👑 Top Müştərilər</div>
                    {detail.topUsers.length === 0
                      ? <div style={s.noData}>Hələ məlumat yoxdur</div>
                      : detail.topUsers.map((u, i) => (
                        <div key={i} style={s.topRow}>
                          <span style={s.rank}>{i + 1}</span>
                          <span style={{ flex: 1 }}>{u.name}</span>
                          <span style={s.badge2}>{u.count} skan</span>
                        </div>
                      ))}
                  </div>
                  {/* Campaigns */}
                  <div style={{ ...s.detailBlock, flex: 1, minWidth: 220 }}>
                    <div style={s.detailTitle}>📢 Son Kampaniyalar</div>
                    {detail.campaigns.length === 0
                      ? <div style={s.noData}>Kampaniya yoxdur</div>
                      : detail.campaigns.map((c, i) => (
                        <div key={i} style={s.campRow}>
                          <FaBullhorn size={12} color="#FF6B35" />
                          <span style={{ flex: 1, fontSize: 13 }}>{c.title || '—'}</span>
                          <span style={s.campDate}>{new Date(c.created_at).toLocaleDateString('az-AZ')}</span>
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            ) : null}
          </td>
        </tr>
      )}
    </>
  );
}

// ─── User row with expandable check-in history ──────────────────────────────
function UserRow({ user, period }) {
  const [expanded, setExpanded] = useState(false);
  const [history, setHistory] = useState(null);
  const [loadingH, setLoadingH] = useState(false);

  const loadHistory = useCallback(async () => {
    if (history) return;
    setLoadingH(true);
    try {
      const { data } = await supabase
        .from('check_ins')
        .select('created_at, business_id, businesses(name, city)')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(20);
      setHistory(data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingH(false);
    }
  }, [user.id, history]);

  const toggle = () => {
    if (!expanded) loadHistory();
    setExpanded(v => !v);
  };

  return (
    <>
      <tr style={s.row} onClick={toggle}>
        <td style={s.td}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={s.expandIcon}>{expanded ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}</span>
            <div>
              <strong>{user.full_name || '—'}</strong>
              <div style={s.tdSub}>@{user.username || '—'}</div>
            </div>
          </div>
        </td>
        <td style={s.td}>
          <span style={{ ...s.badge, background: '#fff3e0', color: '#e65100' }}>
            🪙 {user.points || 0}
          </span>
        </td>
        <td style={s.td}>{user.checkIns}</td>
        <td style={s.td}>{user.qrScans}</td>
        <td style={s.td}>{user.uniqueBusinesses}</td>
        <td style={s.td}>
          {user.lastCheckin
            ? new Date(user.lastCheckin).toLocaleDateString('az-AZ')
            : '—'}
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={6} style={s.expandTd}>
            {loadingH
              ? <div style={{ padding: 16, color: '#999' }}>Yüklənir...</div>
              : (history || []).length === 0
                ? <div style={{ padding: 16, color: '#999' }}>QR skan tarixi yoxdur</div>
                : (
                  <div style={s.expandContent}>
                    <div style={s.detailTitle}>🕒 Son QR Skanlar (son 20)</div>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ background: '#f9fafb' }}>
                          <th style={s.miniTh}>Kafe</th>
                          <th style={s.miniTh}>Şəhər</th>
                          <th style={s.miniTh}>Tarix</th>
                          <th style={s.miniTh}>Saat</th>
                        </tr>
                      </thead>
                      <tbody>
                        {history.map((h, i) => (
                          <tr key={i} style={{ borderBottom: '1px solid #f0f0f0' }}>
                            <td style={s.miniTd}>{h.businesses?.name || '—'}</td>
                            <td style={s.miniTd}>{h.businesses?.city || '—'}</td>
                            <td style={s.miniTd}>{new Date(h.created_at).toLocaleDateString('az-AZ')}</td>
                            <td style={s.miniTd}>{new Date(h.created_at).toLocaleTimeString('az-AZ', { hour: '2-digit', minute: '2-digit' })}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
          </td>
        </tr>
      )}
    </>
  );
}

// ─── Main Page ───────────────────────────────────────────────────────────────
export default function AnalyticsPage() {
  const { getAdminToken } = useAuth();
  const [tab, setTab] = useState('business');
  const [period, setPeriod] = useState('this_month');
  const [loading, setLoading] = useState(true);

  // Business tab state
  const [businesses, setBusinesses] = useState([]);
  const [bizStats, setBizStats] = useState({ totalRevenue: 0, totalScans: 0, totalReservations: 0, totalCampaigns: 0 });

  // User tab state
  const [users, setUsers] = useState([]);
  const [userStats, setUserStats] = useState({ totalPoints: 0, totalScans: 0, activeUsers: 0 });

  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('revenue');

  useEffect(() => {
    if (tab === 'business') loadBusinessAnalytics();
    else loadUserAnalytics();
  }, [tab, period]);

  const loadBusinessAnalytics = async () => {
    setLoading(true);
    setSearch('');
    try {
      const { start, end } = getDateRange(period);

      // All businesses
      const { data: bizList } = await supabase
        .from('businesses')
        .select('id, name, city, is_active')
        .order('name');

      if (!bizList?.length) { setBusinesses([]); setLoading(false); return; }

      const ids = bizList.map(b => b.id);

      // Revenue per business
      const { data: revRes } = await supabase.rpc('admin_get_revenue_logs_full', { p_admin_token: getAdminToken(), p_since: start });
      const revData = (revRes?.success ? revRes.rows : [])
        .filter(r => ids.includes(r.business_id) && r.created_at <= end);

      const revMap = {};
      (revData || []).forEach(r => {
        revMap[r.business_id] = (revMap[r.business_id] || 0) + parseFloat(r.amount || 0);
      });

      // Check-ins per business
      const { data: ciData } = await supabase
        .from('check_ins')
        .select('business_id, user_id, qr_code')
        .in('business_id', ids)
        .gte('created_at', start)
        .lte('created_at', end);

      const ciMap = {};
      const qrMap = {};
      const uniqueUserMap = {};
      (ciData || []).forEach(r => {
        ciMap[r.business_id] = (ciMap[r.business_id] || 0) + 1;
        if (r.qr_code) qrMap[r.business_id] = (qrMap[r.business_id] || 0) + 1;
        if (!uniqueUserMap[r.business_id]) uniqueUserMap[r.business_id] = new Set();
        uniqueUserMap[r.business_id].add(r.user_id);
      });

      // Reservations per business
      const { data: rezData } = await supabase
        .from('reservations')
        .select('business_id')
        .in('business_id', ids)
        .gte('created_at', start)
        .lte('created_at', end);

      const rezMap = {};
      (rezData || []).forEach(r => {
        rezMap[r.business_id] = (rezMap[r.business_id] || 0) + 1;
      });

      // Campaigns per business
      const { data: campData } = await supabase
        .from('campaigns')
        .select('business_id')
        .in('business_id', ids);

      const campMap = {};
      (campData || []).forEach(r => {
        campMap[r.business_id] = (campMap[r.business_id] || 0) + 1;
      });

      const enriched = bizList.map(b => ({
        ...b,
        revenue: revMap[b.id] || 0,
        checkIns: ciMap[b.id] || 0,
        qrScans: qrMap[b.id] || 0,
        reservations: rezMap[b.id] || 0,
        campaigns: campMap[b.id] || 0,
        uniqueUsers: uniqueUserMap[b.id]?.size || 0,
      }));

      const totalRevenue = enriched.reduce((s, b) => s + b.revenue, 0);
      const totalScans = enriched.reduce((s, b) => s + b.checkIns, 0);
      const totalReservations = enriched.reduce((s, b) => s + b.reservations, 0);
      const totalCampaigns = enriched.reduce((s, b) => s + b.campaigns, 0);

      setBizStats({ totalRevenue, totalScans, totalReservations, totalCampaigns });
      setBusinesses(enriched);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const loadUserAnalytics = async () => {
    setLoading(true);
    setSearch('');
    try {
      const { start, end } = getDateRange(period);

      // All users
      const { data: userRes } = await supabase.rpc('admin_get_top_users', { p_admin_token: getAdminToken(), p_limit: 300 });
      const userList = userRes?.success ? userRes.rows : [];

      if (!userList?.length) { setUsers([]); setLoading(false); return; }

      const ids = userList.map(u => u.id);

      // Check-ins in period
      const { data: ciData } = await supabase
        .from('check_ins')
        .select('user_id, business_id, qr_code, created_at')
        .in('user_id', ids)
        .gte('created_at', start)
        .lte('created_at', end)
        .order('created_at', { ascending: false });

      const ciMap = {};
      const qrMapU = {};
      const bizSetMap = {};
      const lastCiMap = {};
      (ciData || []).forEach(r => {
        ciMap[r.user_id] = (ciMap[r.user_id] || 0) + 1;
        if (r.qr_code) qrMapU[r.user_id] = (qrMapU[r.user_id] || 0) + 1;
        if (!bizSetMap[r.user_id]) bizSetMap[r.user_id] = new Set();
        bizSetMap[r.user_id].add(r.business_id);
        if (!lastCiMap[r.user_id]) lastCiMap[r.user_id] = r.created_at;
      });

      const enriched = userList.map(u => ({
        ...u,
        checkIns: ciMap[u.id] || 0,
        qrScans: qrMapU[u.id] || 0,
        uniqueBusinesses: bizSetMap[u.id]?.size || 0,
        lastCheckin: lastCiMap[u.id] || null,
      }));

      const totalPoints = enriched.reduce((s, u) => s + (u.points || 0), 0);
      const totalScans = enriched.reduce((s, u) => s + u.checkIns, 0);
      const activeUsers = enriched.filter(u => u.checkIns > 0).length;

      setUserStats({ totalPoints, totalScans, activeUsers });
      setUsers(enriched);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const sortedBusinesses = [...businesses]
    .filter(b => !search || b.name?.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => {
      if (sortBy === 'revenue') return b.revenue - a.revenue;
      if (sortBy === 'scans') return b.checkIns - a.checkIns;
      if (sortBy === 'reservations') return b.reservations - a.reservations;
      if (sortBy === 'users') return b.uniqueUsers - a.uniqueUsers;
      return 0;
    });

  const sortedUsers = [...users]
    .filter(u => !search || (u.full_name + u.username)?.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => {
      if (sortBy === 'points') return (b.points || 0) - (a.points || 0);
      if (sortBy === 'scans') return b.checkIns - a.checkIns;
      return 0;
    });

  return (
    <div style={s.container}>
      {/* Header */}
      <div style={s.header}>
        <div>
          <h1 style={s.title}>📊 Analitika Paneli</h1>
          <p style={s.subtitle}>Kafe və istifadəçi fəaliyyət statistikası</p>
        </div>
        <div style={s.headerRight}>
          <PeriodSelector value={period} onChange={p => { setPeriod(p); setSortBy(tab === 'business' ? 'revenue' : 'points'); }} />
          <button
            style={s.btnExport}
            onClick={() => {
              if (tab === 'business') {
                exportToCSV(sortedBusinesses.map(b => ({
                  Kafe: b.name, Şəhər: b.city || '', Gəlir: b.revenue.toFixed(2),
                  'QR Skan': b.checkIns, Rezervasiya: b.reservations,
                  Kampaniya: b.campaigns, 'Unikal Müştəri': b.uniqueUsers,
                })), 'kafe_analitika.csv');
              } else {
                exportToCSV(sortedUsers.map(u => ({
                  Ad: u.full_name, Username: u.username,
                  Xal: u.points || 0, 'QR Skan': u.checkIns,
                  'Unikal Kafe': u.uniqueBusinesses, 'Son Aktivlik': u.lastCheckin || '',
                })), 'user_analitika.csv');
              }
            }}
          >
            <FaFileExcel /> Export
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div style={s.tabs}>
        <button style={tab === 'business' ? s.tabActive : s.tab} onClick={() => { setTab('business'); setSortBy('revenue'); setSearch(''); }}>
          <FaStore /> Kafe Analitikası
        </button>
        <button style={tab === 'user' ? s.tabActive : s.tab} onClick={() => { setTab('user'); setSortBy('points'); setSearch(''); }}>
          <FaUsers /> İstifadəçi Analitikası
        </button>
      </div>

      {loading ? (
        <div style={s.loading}><div style={s.spinner} /></div>
      ) : tab === 'business' ? (
        <>
          {/* Biz Stats */}
          <div style={s.statsGrid}>
            <StatCard icon={FaMoneyBillWave} label="Ümumi Gəlir" value={`${bizStats.totalRevenue.toFixed(2)} ₼`} color="#06D6A0" sub="Seçilmiş period" />
            <StatCard icon={FaQrcode}        label="QR Skan"     value={bizStats.totalScans}           color="#FF6B35" sub="Check-in sayı" />
            <StatCard icon={FaCalendarCheck} label="Rezervasiya" value={bizStats.totalReservations}    color="#667eea" sub="Period ərzində" />
            <StatCard icon={FaBullhorn}      label="Kampaniyalar" value={bizStats.totalCampaigns}      color="#FFD166" sub="Ümumi sayı" />
          </div>

          {/* Sort & Search */}
          <div style={s.toolbar}>
            <input
              placeholder="Kafe axtar..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={s.searchInput}
            />
            <select value={sortBy} onChange={e => setSortBy(e.target.value)} style={s.select}>
              <option value="revenue">Gəlirə görə</option>
              <option value="scans">QR Skan-a görə</option>
              <option value="reservations">Rezervasiyaya görə</option>
              <option value="users">Müştəri sayına görə</option>
            </select>
          </div>

          {/* Table */}
          <div style={s.tableWrap}>
            <table style={s.table}>
              <thead>
                <tr style={s.thead}>
                  <th style={s.th}>Kafe</th>
                  <th style={s.th}>Gəlir</th>
                  <th style={s.th}>Check-in</th>
                  <th style={s.th}>QR Skan</th>
                  <th style={s.th}>Rezervasiya</th>
                  <th style={s.th}>Kampaniya</th>
                  <th style={s.th}>Unikal Müştəri</th>
                </tr>
              </thead>
              <tbody>
                {sortedBusinesses.length === 0
                  ? <tr><td colSpan={7} style={s.noData}>Məlumat yoxdur</td></tr>
                  : sortedBusinesses.map(b => <BusinessRow key={b.id} biz={b} period={period} />)
                }
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <>
          {/* User Stats */}
          <div style={s.statsGrid}>
            <StatCard icon={FaStar}   label="Ümumi Xal"       value={userStats.totalPoints.toLocaleString()} color="#FFD166" sub="Bütün userlər" />
            <StatCard icon={FaQrcode} label="QR Skan"          value={userStats.totalScans}                  color="#FF6B35" sub="Period ərzində" />
            <StatCard icon={FaFire}   label="Aktiv İstifadəçi" value={userStats.activeUsers}                 color="#06D6A0" sub="Ən az 1 skan" />
          </div>

          {/* Sort & Search */}
          <div style={s.toolbar}>
            <input
              placeholder="İstifadəçi axtar..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={s.searchInput}
            />
            <select value={sortBy} onChange={e => setSortBy(e.target.value)} style={s.select}>
              <option value="points">Xala görə</option>
              <option value="scans">QR Skan-a görə</option>
            </select>
          </div>

          {/* Table */}
          <div style={s.tableWrap}>
            <table style={s.table}>
              <thead>
                <tr style={s.thead}>
                  <th style={s.th}>İstifadəçi</th>
                  <th style={s.th}>Xal</th>
                  <th style={s.th}>Check-in</th>
                  <th style={s.th}>QR Skan</th>
                  <th style={s.th}>Unikal Kafe</th>
                  <th style={s.th}>Son Aktivlik</th>
                </tr>
              </thead>
              <tbody>
                {sortedUsers.length === 0
                  ? <tr><td colSpan={6} style={s.noData}>Məlumat yoxdur</td></tr>
                  : sortedUsers.map(u => <UserRow key={u.id} user={u} period={period} />)
                }
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

const s = {
  container: { maxWidth: 1600, margin: '0 auto' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28, flexWrap: 'wrap', gap: 16 },
  title: { fontSize: 30, fontWeight: 'bold', color: '#1A1A2E', marginBottom: 4 },
  subtitle: { fontSize: 14, color: '#999' },
  headerRight: { display: 'flex', gap: 12, alignItems: 'center' },
  tabs: { display: 'flex', gap: 8, marginBottom: 24 },
  tab: { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px', background: '#F3F4F6', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 600, color: '#555', cursor: 'pointer', transition: 'all 0.2s' },
  tabActive: { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px', background: '#FF6B35', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 600, color: '#fff', cursor: 'pointer' },
  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16, marginBottom: 24 },
  statCard: { background: '#fff', borderRadius: 12, padding: '20px 24px', boxShadow: '0 2px 8px rgba(0,0,0,0.07)', display: 'flex', gap: 16, alignItems: 'center' },
  statIcon: { width: 48, height: 48, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  statLabel: { fontSize: 12, color: '#999', fontWeight: 600, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.5px' },
  statValue: { fontSize: 26, fontWeight: 'bold', color: '#1A1A2E' },
  statSub: { fontSize: 12, color: '#bbb', marginTop: 2 },
  toolbar: { display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' },
  searchInput: { flex: 1, minWidth: 200, padding: '10px 14px', border: '2px solid #E0E0E0', borderRadius: 8, fontSize: 14, outline: 'none' },
  select: { padding: '10px 14px', border: '2px solid #E0E0E0', borderRadius: 8, fontSize: 14, outline: 'none', background: '#fff', cursor: 'pointer' },
  btnExport: { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 18px', background: '#4CAF50', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  tableWrap: { background: '#fff', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.07)', overflow: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse', minWidth: 700 },
  thead: { background: '#F8F9FA' },
  th: { padding: '14px 16px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: '#666', textTransform: 'uppercase', borderBottom: '2px solid #E0E0E0', letterSpacing: '0.5px' },
  row: { borderBottom: '1px solid #F3F4F6', cursor: 'pointer', transition: 'background 0.15s' },
  td: { padding: '14px 16px', fontSize: 14, color: '#1A1A2E', verticalAlign: 'middle' },
  tdSub: { fontSize: 12, color: '#999', marginTop: 2 },
  expandIcon: { color: '#999', display: 'flex', alignItems: 'center' },
  badge: { display: 'inline-block', padding: '4px 10px', borderRadius: 20, fontSize: 13, fontWeight: 700 },
  expandTd: { padding: 0, background: '#FAFBFF' },
  expandContent: { padding: '20px 24px' },
  detailBlock: { background: '#fff', borderRadius: 10, padding: 16, border: '1px solid #F0F0F0', marginBottom: 16 },
  detailTitle: { fontSize: 14, fontWeight: 700, color: '#1A1A2E', marginBottom: 12 },
  topRow: { display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', borderBottom: '1px solid #F5F5F5', fontSize: 13 },
  rank: { width: 22, height: 22, borderRadius: '50%', background: '#FF6B35', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, flexShrink: 0 },
  badge2: { fontSize: 12, background: '#FFF3E0', color: '#E65100', padding: '2px 8px', borderRadius: 10, fontWeight: 600 },
  campRow: { display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: '1px solid #F5F5F5', fontSize: 13 },
  campDate: { fontSize: 11, color: '#bbb' },
  noData: { padding: '40px', textAlign: 'center', color: '#bbb', fontSize: 14 },
  miniTh: { padding: '8px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#888', textTransform: 'uppercase', borderBottom: '1px solid #E0E0E0' },
  miniTd: { padding: '8px 12px', fontSize: 13, color: '#444' },
  loading: { display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' },
  spinner: { width: 44, height: 44, border: '4px solid #f3f3f3', borderTop: '4px solid #FF6B35', borderRadius: '50%', animation: 'spin 1s linear infinite' },
};
